import { ValidCookieSubcategory } from './detectBlockedElements';

export type PlaceholderTheme = 'light' | 'dark' | 'brand';

export type PlaceholderProps =
  // at least one, but not both
  (
    | {
        blockedContentSubcategory: ValidCookieSubcategory;
        blockedElement?: never;
      }
    | {
        blockedElement: HTMLElement;
        blockedContentSubcategory?: never;
      }
  ) & {
    acceptButtonText?: string;
    acceptText?: string;
    analyticsText?: string;
    functionalText?: string;
    marketingText?: string;
    unclassifiedText?: string;
    buttonRadius: `${string}px`;
    cardRadius: `${string}px`;
    displayStyle: CSSStyleDeclaration['display'];
    height: number; // in px
    primaryColor: string;
    secondaryColor: string;
    theme: PlaceholderTheme;
    width: number; // in px
    onclick?: () => void;
  };

function capitalize(s: string) {
  if (s) {
    return s[0].toUpperCase() + s.slice(1);
  }
  return s;
}

function toTitleCase(s: string) {
  if (!s) {
    return '';
  }
  return s
    .replace(/^custom_/, '')
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .split(' ')
    .map(capitalize)
    .join(' ');
}

const getLuminance = (hexColor: string): number => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  const rgb = parseInt(hex, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;

  // Convert to relative luminance using WCAG formula
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastTextColor = (backgroundColor: string): 'black' | 'white' => {
  const luminance = getLuminance(backgroundColor);
  // Using WCAG recommended contrast ratio threshold
  return luminance > 0.179 ? 'black' : 'white';
};

const placeholderStyles = {
  light: {
    placeholderColor: '#e5e7eb', // gray-200
    buttonColor: 'black',
  },
  dark: {
    placeholderColor: '#030712' /* gray-950 */,
    buttonColor: 'white',
  },
  brand: {
    placeholderColor: '#030712' /* gray-950 */,
    buttonColor: '#057bfa', // blue-500
  },
};

const placeholderText = {
  acceptButton: 'Accept',
  acceptText:
    'Content blocked by privacy settings. To view, accept this consent category: ',
  analytics: 'Analytics',
  functional: 'Functional',
  marketing: 'Marketing',
  unclassified: 'Unclassified',
};

export const createPlaceholderComponent = ({
  acceptButtonText,
  acceptText,
  analyticsText,
  functionalText,
  marketingText,
  unclassifiedText,
  blockedContentSubcategory,
  blockedElement,
  buttonRadius,
  cardRadius,
  displayStyle,
  height,
  primaryColor,
  secondaryColor,
  theme,
  width,
  onclick,
}: PlaceholderProps): HTMLDivElement => {
  height = Math.max(height, 175);
  width = Math.max(width, 500);

  const placeholderColor =
    theme === 'brand' && secondaryColor
      ? secondaryColor
      : placeholderStyles[theme].placeholderColor;
  const buttonColor =
    theme === 'brand' && primaryColor
      ? primaryColor
      : placeholderStyles[theme].buttonColor;

  /* Only calculate contrast colors for brand theme */
  const buttonTextColor =
    theme === 'brand'
      ? getContrastTextColor(buttonColor)
      : theme === 'light'
        ? 'white'
        : 'black';

  const placeholderTextColor =
    theme === 'brand'
      ? getContrastTextColor(placeholderColor)
      : theme === 'light'
        ? 'black'
        : 'white';

  /* Placeholder mimics style of component its replacing to respect client layout */
  const placeholder = Object.assign(document.createElement('div'), {
    className: 'placeholder',
  });
  Object.assign(placeholder.style, {
    height: `${height}px`,
    width: `${width}px`,
    display: displayStyle,
    zIndex: '800',
    position: 'relative',
    background: 'transparent',
  });

  /* Container provides custom styling to center button and alert */
  const container = document.createElement('div');
  Object.assign(container.style, {
    borderRadius: cardRadius,
    width: '100%',
    height: '100%',
    backgroundColor: placeholderColor,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  });
  placeholder.append(container);

  const alert = document.createElement('div');
  Object.assign(alert.style, {
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  });
  container.append(alert);

  let cookieSubcategory: ValidCookieSubcategory = 'functional'; // default value
  if (blockedContentSubcategory !== undefined) {
    cookieSubcategory = blockedContentSubcategory;
  } else if (blockedElement !== undefined) {
    cookieSubcategory = (blockedElement.getAttribute('data-concord-category') ??
      'functional') as ValidCookieSubcategory;
  }

  const acceptButtonContent = acceptButtonText ?? placeholderText.acceptButton;
  const acceptTextContent = acceptText ?? placeholderText.acceptText;
  const subcategoryText = (() => {
    switch (cookieSubcategory) {
      case 'analytics':
        return analyticsText ?? placeholderText.analytics;
      case 'functional':
        return functionalText ?? placeholderText.functional;
      case 'marketing':
        return marketingText ?? placeholderText.marketing;
      case 'unclassified':
        return unclassifiedText ?? placeholderText.unclassified;
      default:
        return toTitleCase(cookieSubcategory);
    }
  })();

  const text = Object.assign(document.createElement('p'), {
    innerText: `${acceptTextContent} ${subcategoryText}`,
  });
  Object.assign(text.style, {
    color: placeholderTextColor,
    width: 'auto',
    paddingBlock: '16px',
    paddingInline: '32px',
    fontSize: '1rem',
  });

  const enableCookiesButton = Object.assign(document.createElement('button'), {
    type: 'button',
    innerText: acceptButtonContent,
    onclick: onclick ?? (() => {}),
  });
  Object.assign(enableCookiesButton.style, {
    backgroundColor: buttonColor,
    borderRadius: buttonRadius,
    color: buttonTextColor,
    paddingBlock: '8px',
    paddingInline: '16px',
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '8px',
  });
  alert.append(text, enableCookiesButton);

  return placeholder;
};
